import type { ISbStoryData } from '@storyblok/js'
import type {
  ProductOffer as StoryblokProductOffer,
  Tag
} from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import {
  PrimaryButtonLink,
  ProductOffer,
  ProductOfferType
} from '@percent/cause-dashboard/app/productOffers/data/data.types'
import { LocaleKey } from '@percent/cause-dashboard/i18n/config'
import { resolveNativeApplyPartnerKey } from '@percent/cause-dashboard/constants/nativeApply/resolveNativeApplyPartner'
import type { NativeApplyModalContext } from '@percent/cause-dashboard/constants/nativeApply/getResolvedNativeApplyContext'

/** When secondary CTA label is missing or API sends an unexpected value at runtime. */
const sbCtaTypeFallbackLabel: Record<StoryblokProductOffer['primaryCtaType'], LocaleKey> = {
  calendly: 'productOffers.bookCall',
  typeform: 'productOffers.signUpHere',
  external: 'productOffers.learnMore',
  internal: 'productOffers.learnMore',
  native_apply: 'productOffers.apply',
  register_interest: 'productOffers.learnMore'
}

/** Storyblok single-option values → app `LocaleKey` (must match `storyblok-components` literals). */
const sbCtaLabelToLocaleKey: Record<NonNullable<StoryblokProductOffer['primaryCtaLabel']>, LocaleKey> = {
  apply: 'productOffers.apply',
  get_started: 'productOffers.getStarted',
  learn_more: 'productOffers.learnMore',
  buy_now: 'productOffers.buyNow',
  start_free_trial: 'productOffers.startFreeTrial',
  get_started_free: 'productOffers.getStartedFree',
  book_your_call: 'productOffers.bookCall',
  sign_up_here: 'productOffers.signUpHere',
  book_a_call: 'button.bookACall',
  join_the_waitlist_today: 'productOffers.joinTheWaitlistToday',
  im_interested: 'productOffers.imInterested'
}

type SbCtaInput = {
  ctaType: StoryblokProductOffer['primaryCtaType']
  ctaLabel: StoryblokProductOffer['primaryCtaLabel'] | StoryblokProductOffer['secondaryCtaLabel']
  ctaLink: string | undefined
  /** Storyblok exposes a single optional `typeformId` on the blok; used when this CTA is typeform. */
  typeformId?: string
  nativeApplyPartner?: number | string
}

function linkTextFromSb(rawLabel: SbCtaInput['ctaLabel'], ctaType: SbCtaInput['ctaType']): LocaleKey {
  if (rawLabel && rawLabel in sbCtaLabelToLocaleKey) {
    return sbCtaLabelToLocaleKey[rawLabel as NonNullable<StoryblokProductOffer['primaryCtaLabel']>]
  }
  return sbCtaTypeFallbackLabel[ctaType]
}

/**
 * Maps a single Storyblok CTA to a `PrimaryButtonLink`, or returns `null` when the CTA is incomplete
 * (missing link for link-style CTAs or unresolvable partner key for native_apply). Callers decide how
 * to handle the `null` — we drop the offer entirely for primary CTAs and just clear secondaries.
 */
function sbCtaToProductOfferCta(
  sb: SbCtaInput,
  modalContext: NativeApplyModalContext | null
): PrimaryButtonLink | null {
  const linkText = linkTextFromSb(sb.ctaLabel, sb.ctaType)
  const trimmedLink = sb.ctaLink?.trim() ?? ''

  switch (sb.ctaType) {
    case 'native_apply': {
      const partnerKey = resolveNativeApplyPartnerKey(sb.nativeApplyPartner)
      if (!partnerKey || !modalContext) {
        return null
      }
      return {
        linkType: 'native_apply',
        // Fallback link opened instead of the modal when the organisation is not active.
        link: trimmedLink,
        linkText,
        partnerKey,
        modalContext
      }
    }
    case 'typeform': {
      if (!trimmedLink) return null
      return {
        linkType: 'typeform',
        link: trimmedLink,
        linkText,
        typeformId: sb.typeformId?.trim() ?? ''
      }
    }
    case 'calendly':
      if (!trimmedLink) return null
      return { linkType: 'calendly', link: trimmedLink, linkText }
    case 'internal':
      if (!trimmedLink) return null
      return { linkType: 'internal', link: trimmedLink, linkText }
    case 'external':
      if (!trimmedLink) return null
      return { linkType: 'external', link: trimmedLink, linkText }
    case 'register_interest':
      return { linkType: 'register_interest', link: '', linkText }
  }
}

const sbTypeToApp: Record<StoryblokProductOffer['type'], ProductOfferType> = {
  PercentService: ProductOfferType.PercentService,
  PartnerProduct: ProductOfferType.PartnerProduct,
  PercentProduct: ProductOfferType.PercentProduct
}

function searchTagToString(entry: ISbStoryData<Tag> | string): string | null {
  if (typeof entry === 'string') {
    const s = entry.trim()
    return s || null
  }
  if (entry && typeof entry === 'object' && 'content' in entry) {
    const content = (entry as ISbStoryData<Tag>).content
    if (content?.component === 'tag') {
      const v = content.value?.trim() || content.name?.trim()
      return v || null
    }
  }
  return null
}

function mapSearchTags(searchTags: StoryblokProductOffer['searchTags'] | undefined): string[] {
  if (!Array.isArray(searchTags)) return []
  const out: string[] = []
  for (const item of searchTags) {
    const s = searchTagToString(item as ISbStoryData<Tag> | string)
    if (s) out.push(s)
  }
  return out
}

/** Storyblok `allowedCountries` is a comma-separated list (e.g. `POL,USA,GB`). */
export function mapAllowedCountries(raw: StoryblokProductOffer['allowedCountries'] | undefined): string[] | undefined {
  if (raw === undefined || raw === null) return undefined
  const trimmed = typeof raw === 'string' ? raw.trim() : ''
  if (!trimmed) return undefined
  const codes = trimmed
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
  return codes.length > 0 ? codes : undefined
}

/**
 * Self-referential modal context: when a tile's CTA is `native_apply`, the modal's logo / title /
 * description are pulled from the tile's own offer fields (not from a separate relation).
 */
function selfModalContext(c: StoryblokProductOffer): NativeApplyModalContext | null {
  const logoUrl = c.logo?.filename?.trim() ?? ''
  const title = c.title?.trim() ?? ''
  const description = c.description?.trim() ?? ''
  if (!logoUrl || !title || !description) {
    return null
  }
  return { logoUrl, title, description }
}

/**
 * Maps a resolved Storyblok `product_offer` story into the app's `ProductOffer` shape. Returns
 * `null` when the primary CTA cannot be built (missing link, unresolvable native-apply partner,
 * or self-context with empty logo/title/description) — drop the tile rather than render a broken CTA.
 */
export function mapStoryblokProductOfferToProductOffer(
  story: ISbStoryData<StoryblokProductOffer>
): ProductOffer | null {
  const c = story.content
  const modalContext = selfModalContext(c)

  const primaryButton = sbCtaToProductOfferCta(
    {
      ctaType: c.primaryCtaType,
      ctaLabel: c.primaryCtaLabel,
      ctaLink: c.primaryCtaLink,
      typeformId: c.typeformId,
      nativeApplyPartner: c.primaryCtaNativeApplyPartner
    },
    modalContext
  )

  if (!primaryButton) {
    return null
  }

  const secondaryButton =
    c.secondaryCtaType !== undefined
      ? sbCtaToProductOfferCta(
          {
            ctaType: c.secondaryCtaType,
            ctaLabel: c.secondaryCtaLabel,
            ctaLink: c.secondaryCtaLink,
            typeformId: c.typeformId,
            nativeApplyPartner: c.secondaryCtaNativeApplyPartner
          },
          modalContext
        ) ?? undefined
      : undefined

  const logoUrl = c.logo?.filename ?? undefined

  return {
    offerId: story.uuid,
    type: sbTypeToApp[c.type] ?? ProductOfferType.PartnerProduct,
    name: c.title,
    tracking: c.tracking,
    description: c.description,
    logo: logoUrl,
    primaryButton,
    secondaryButton,
    allowedCountries: mapAllowedCountries(c.allowedCountries),
    searchTags: mapSearchTags(c.searchTags),
    ...(c.claimApprovedOnly === true ? { claimApprovedOnly: true } : {}),
    ...(c.experiment_variant ? { experimentVariant: c.experiment_variant } : {})
  }
}
